import { GetTokenSilentlyOptions } from '@auth0/auth0-react'

// Resolves undefined where the session holds no token to send: an EMR-embedded session whose EMR token has
// expired, and the unauthenticated handoff-code exchange that establishes one. The caller then sends the
// request with no bearer rather than reading it as a dead Auth0 session.
type GetAccessTokenSilentlyHandler = (
  options?: GetTokenSilentlyOptions | undefined,
) => Promise<string | undefined>

type ReauthHandler = () => void

let getAccessTokenSilentlyHandler: null | GetAccessTokenSilentlyHandler = null
let reauthHandler: null | ReauthHandler = null

// Latches once the first re-auth has been requested. A dead session surfaces
// from both the silent-token path and a backend 401, often on several in-flight
// queries at once; without this guard each one would fire its own redirect.
// The interactive login navigates away and reloads the app, which resets this back
// to false, so a genuinely new stale session in a later session still recovers.
let reauthRequested = false

// Bridges the out-of-React RTK Query base query to the session, which is only
// reachable through the `useRegardAuth` hook inside the React tree.
export const sec = {
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) =>
    getAccessTokenSilentlyHandler?.(options),

  setAccessTokenSilentlyHandler: (func: GetAccessTokenSilentlyHandler) => {
    getAccessTokenSilentlyHandler = func
  },

  setReauthHandler: (func: ReauthHandler) => {
    reauthHandler = func
  },

  /**
   * Requests interactive re-authentication exactly once per session.
   *
   * Called when the session can no longer produce a valid token — either the
   * silent token call threw a re-auth-required error or the backend rejected a
   * request with 401. Delegates to the handler the React tree registered
   * (`useRegardAuth().reauth`, which recovers the session however its mode
   * recovers one). No-op if no handler is registered yet (e.g. the E2E bypass)
   * or a redirect is already underway.
   */
  requestReauth: () => {
    if (reauthRequested || reauthHandler === null) {
      return
    }
    reauthRequested = true
    reauthHandler()
  },
}
